/**
 * Opens and closes the harmonised guarantee notice.
 *
 * Built on the native <dialog>, which brings focus trapping, the Escape key
 * and the backdrop with it - the module only has to wire the triggers. All
 * handlers are delegated from document, so a notice rendered later (widget,
 * ajax reload) needs no re-initialisation, and no markup carries an inline
 * handler that a content security policy would block.
 */

const OPEN_ATTRIBUTE = 'data-trwgl-open';
const CLOSE_ATTRIBUTE = 'data-trwgl-close';
const LAZY_ATTRIBUTE = 'data-trwgl-src';

/**
 * Loads the notice artwork the first time its dialog is opened.
 *
 * The official vector weighs several hundred kB. Carrying that on every page
 * view - the notice sits in the footer, so that is every single one - to serve
 * the few visitors who open it would be the wrong trade. The attribute is
 * dropped afterwards, so this runs once.
 *
 * @param {Element} dialog
 */
function loadArtwork(dialog) {
    const images = dialog.querySelectorAll('[' + LAZY_ATTRIBUTE + ']');

    Array.prototype.forEach.call(images, function (image) {
        image.setAttribute('src', image.getAttribute(LAZY_ATTRIBUTE));
        image.removeAttribute(LAZY_ATTRIBUTE);
    });
}

/**
 * Opens the notice dialog with the given id.
 *
 * @param {string} dialogId
 * @returns {boolean} whether a dialog was found and opened
 */
export function trwGuaranteeLabelOpen(dialogId) {
    const dialog = document.getElementById(dialogId);

    if (!dialog) {
        return false;
    }

    loadArtwork(dialog);

    if (typeof dialog.showModal === 'function') {
        dialog.showModal();
    } else {
        // Browsers without dialog support still show the content as a block.
        dialog.setAttribute('open', 'open');
    }

    return true;
}

/**
 * Closes the notice dialog the given element sits in.
 *
 * @param {Element} element
 */
export function trwGuaranteeLabelClose(element) {
    const dialog = element.closest('dialog');

    if (!dialog) {
        return;
    }

    if (typeof dialog.close === 'function') {
        dialog.close();
    } else {
        dialog.removeAttribute('open');
    }
}

function onClick(event) {
    const trigger = event.target.closest('[' + OPEN_ATTRIBUTE + ']');

    if (trigger) {
        event.preventDefault();
        trwGuaranteeLabelOpen(trigger.getAttribute(OPEN_ATTRIBUTE));
        return;
    }

    if (event.target.closest('[' + CLOSE_ATTRIBUTE + ']')) {
        event.preventDefault();
        trwGuaranteeLabelClose(event.target);
        return;
    }

    // A click that reaches the dialog element itself hit the backdrop - the
    // content sits in an inner element and would have been the target instead.
    if (event.target.matches('dialog.trwgl-dialog')) {
        trwGuaranteeLabelClose(event.target);
    }
}

document.addEventListener('click', onClick);
